import { z } from 'zod';

/**
 * Lenient version of PLACE_INPUT that accepts `null` as well as `undefined`.
 * After a JSON round-trip through sessionStorage, `undefined` values are
 * stripped while `null` values are preserved. The strict form-level
 * PLACE_INPUT uses `.optional()` which rejects `null`, so we use a dedicated
 * schema here to tolerate both.
 */
const sessionPlaceInput = z.object({
    addressID: z.string().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    region: z.string().nullish(),
    suburb: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postCode: z.string().nullish(),
});

export const enquiryFormSessionSchema = z.object({
    name: z.string().trim().min(1),
    email: z.string().trim().email(),
    // Phone and postcode are optional here: some enquiry forms (e.g. the
    // project "Get in touch" form) collect neither, and their saved session
    // must still pass validation for post-login auto-submit to fire.
    phoneNumber: z.string().trim().optional().default(''),
    postcode: z
        .object({ header: z.string(), value: sessionPlaceInput })
        .nullish(),
    branchId: z.number().int().positive().nullish(),
    buildHubId: z.number().int().positive().nullish(),
    message: z.string().trim().min(1),
    siteTreeId: z.number().int().positive(),
    // Bulk-enquire selection: persisted so post-login auto-submit re-sends every
    // selected item, not just the single keyed one. Absent for single enquiries.
    siteTreeIds: z.array(z.number().int().positive()).nullish(),
    source: z
        .enum([
            'InPage',
            'EnquiryModal',
            'ProfessionalIsland',
            'ProductIsland',
            'ProjectIsland',
            'ProjectTeam',
            'ProjectTeamSection',
            'ProjectDetail',
            'ProjectMobileCTA',
            'ProjectAboutProfessional',
            'ProductHeaderCTA',
            'ProductDetailsCTA',
            'ProductPhotoViewer',
            'ProductMobileCTA',
            'ProfessionalHeader',
            'ProfessionalOverview',
            'ProfessionalAboutTab',
            'ProfessionalLocationDetail',
            'ProfessionalLocationMap',
        ])
        // Source is tracking metadata; an unrecognised value (e.g. a session
        // written by a newer bundle) must never veto the enquiry itself.
        .catch('EnquiryModal'),
    professional: z.object({
        ID: z.number().int().positive(),
        Title: z.string(),
    }),
    itemType: z.enum(['professional', 'product', 'project', 'article']),
    relatedItem: z
        .object({
            id: z.number().int().positive(),
            type: z.enum(['project']),
            title: z.string(),
            category: z.string(),
        })
        .nullish(),
    files: z
        .array(
            z.object({
                name: z.string(),
                type: z.string(),
                size: z.number(),
                lastModified: z.number(),
            }),
        )
        .nullish(),
    isModal: z.boolean().default(false),
});
